import React, { useState } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { LmsStudentSidebar } from './LmsStudentSidebar';
import { clsx } from 'clsx';
import { useAuth } from '../../contexts/AuthContext';
import { computeShouldRenderSidebar, isLmsStudentSession } from '../../utils/lmsSession';
// import { Footer } from './Footer';

interface LayoutProps {
  children: React.ReactNode;
  showSidebar?: boolean;
  /** When true, main content has no padding (e.g. AI Tutor Live full-bleed). */
  noPadding?: boolean;
  /**
   * When true, the shell height follows the parent (h-full) instead of locking to the viewport (h-dvh).
   * Use when Layout is nested under extra UI (e.g. Communication Tutor stats strip) so the bottom
   * control bar is not clipped by overflow-hidden.
   */
  fillParent?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  showSidebar = true,
  noPadding = false,
  fillParent = false,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, isLmsLaunchSession } = useAuth();

  const shouldRenderSidebar = computeShouldRenderSidebar(showSidebar);
  const isLmsStudent = isLmsStudentSession(user, isLmsLaunchSession);

  const closeSidebar = () => setSidebarOpen(false);

  const sidebarContent = isLmsStudent ? (
    <LmsStudentSidebar onNavigate={closeSidebar} />
  ) : (
    <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
  );

  const mobileSidebarContent = isLmsStudent ? (
    <LmsStudentSidebar onNavigate={closeSidebar} mobile />
  ) : (
    <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
  );

  return (
    <div
      className={clsx(
        'overflow-hidden flex flex-col bg-app-bg dark:bg-gray-950',
        fillParent ? 'h-full min-h-0 max-h-dvh' : 'h-dvh max-h-screen'
      )}
    >
      <header className="h-16 shrink-0">
        <Header
          onMenuClick={
            shouldRenderSidebar ? () => setSidebarOpen((prev) => !prev) : undefined
          }
        />
      </header>

      {sidebarOpen && shouldRenderSidebar && (
        <div
          className="fixed inset-0 top-16 z-30 bg-black/40 md:hidden"
          onClick={closeSidebar}
        />
      )}

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {shouldRenderSidebar && (
          <aside
            className={clsx(
              'shrink-0 border-r border-app-border dark:border-gray-700 bg-app-surface dark:bg-gray-900 h-full min-h-0',
              'hidden md:block md:sticky md:top-16 md:h-[calc(100dvh-64px)] md:max-h-[calc(100vh-64px)] transition-[width] duration-200',
              sidebarOpen ? 'md:w-64' : 'md:w-0 md:overflow-hidden md:border-r-0'
            )}
          >
            {sidebarContent}
          </aside>
        )}

        <main className="flex-1 min-w-0 min-h-0 overflow-hidden bg-app-bg dark:bg-gray-950 text-app-text dark:text-gray-100 flex flex-col">
          <div className={clsx('flex-1 min-h-0 overflow-y-auto w-full', !noPadding && 'p-4 sm:p-6 lg:p-8')}>{children}</div>
        </main>
      </div>

      {shouldRenderSidebar && (
        <aside
          className={clsx(
            'fixed left-0 top-16 bottom-0 z-40 w-64 border-r border-app-border dark:border-gray-700 bg-app-surface dark:bg-gray-900 md:hidden',
            'transform transition-transform duration-200',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {mobileSidebarContent}
        </aside>
      )}
      {/* <Footer /> */}
    </div>
  );
};
